// Copyright 2025-2026 NXGN Management, LLC. All Rights Reserved.
import { useMemo } from 'react'
import {
  BasicMenu,
  Button,
  ColumnDef,
  Flex,
  MenuOption,
  Note,
  Table,
  Typography,
  useShowHide,
} from '@teamnine/pcj-ui-v2'
import { KeyboardArrowDown, Article } from '@/utils/material-icons'
import { useParams } from 'react-router-dom'
import { useTheme } from 'styled-components'

import {
  TemplateFragment,
  downloadTemplate,
  downloadObjectDataPackage,
} from '@/services/templates'
import { formatDateTime } from '@/utils/date'
import { formatName } from '@/utils/helpers'
import Page from '@/app/shared/components/Layout/Page'
import DetailItem from '@/app/shared/components/DetailItem'
import TemplateStatusTag from '@/app/Templates/components/TemplateStatusTag'
import StatusTag from '@/app/shared/components/StatusTag'
import UpdateTemplateModal from '@/app/Templates/components/UpdateTemplateModal'
import ViewTemplateModal from '@/app/Templates/components/ViewTemplateModal'
import DeleteTemplateModal from '@/app/Templates/components/DeleteTemplateModal'
import UpdateGoldenStatusModal from '@/app/Templates/components/UpdateGoldenStatusModal'
import PublishTemplateModal from '@/app/Templates/components/PublishTemplateModal'
import UploadObjectDataModal from '@/app/Templates/components/UploadObjectDataModal'
import PageHeader from '@/app/shared/components/PageHeader'
import Panel from '@/app/shared/components/Panel'
import { useGetTemplate } from '@/app/Templates/hooks/useGetTemplate'

export default function Template() {
  const { colors } = useTheme()
  const { templateId } = useParams()

  const { data: currentTemplate, isLoading } = useGetTemplate(templateId)

  const updateTemplateModal = useShowHide()
  const viewTemplateModal = useShowHide()
  const deleteTemplateModal = useShowHide()
  const markGoldenModal = useShowHide()
  const unmarkGoldenModal = useShowHide()
  const publishTemplateModal = useShowHide()
  const uploadObjectDataModal = useShowHide()
  const isDraft = !currentTemplate?.published
  const isGolden = currentTemplate?.golden
  const templateViewable =
    currentTemplate?.templateFragments?.every(
      (templateFragment) => templateFragment.status.toLowerCase() === 'success'
    ) ?? false
  const templatePublishable =
    templateViewable &&
    currentTemplate?.objectData.filter(
      (objectData) =>
        objectData.status.toLowerCase() !== 'success' &&
        objectData.status.toLowerCase() !== 'no_records'
    ).length === 0

  const fragments = useMemo(() => {
    return [...(currentTemplate?.templateFragments ?? [])].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    )
  }, [currentTemplate?.templateFragments])

  const fragmentColumns: ColumnDef<TemplateFragment>[] = useMemo(
    () => [
      {
        id: 'name',
        header: 'Service',
        accessorKey: 'name' as const,
        enableSorting: false,
        maxSize: 400,
        cell: ({ row }) => (
          <Typography color={colors.ngprimary950}>
            {formatName(row.original.name)}
          </Typography>
        ),
      },
      {
        id: 'message',
        header: 'Message',
        accessorKey: 'message' as const,
        enableSorting: false,
        maxSize: 400,
        cell: ({ row }) => <Typography>{row.original.errorMessage}</Typography>,
      },
      {
        id: 'status',
        header: 'Status',
        accessorKey: 'status' as const,
        enableSorting: false,
        maxSize: 400,
        cell: ({ row }) => <StatusTag status={row.original.status} />,
      },
    ],
    [colors.ngprimary950]
  )

  const menuOptions = (
    [
      {
        label: 'View',
        onSelect: viewTemplateModal.show,
        disabled: !templateViewable,
      },
      !isGolden && {
        label: 'Edit',
        onSelect: updateTemplateModal.show,
      },
      {
        label: 'Download',
        onSelect: () => {
          if (!currentTemplate) return
          downloadTemplate(currentTemplate)
        },
        disabled: !templateViewable,
      },
      isGolden
        ? {
            label: 'Unmark Golden',
            onSelect: unmarkGoldenModal.show,
          }
        : !isDraft && {
            label: 'Mark Golden',
            onSelect: markGoldenModal.show,
          },
      isDraft && {
        label: 'Publish',
        onSelect: publishTemplateModal.show,
        disabled: !templatePublishable,
      },
      !isGolden && {
        label: 'Delete',
        onSelect: deleteTemplateModal.show,
      },
      !isGolden && {
        label: 'Add Object Data',
        onSelect: uploadObjectDataModal.show,
      },
      currentTemplate?.includesObjectData && {
        label: 'Download Object Data',
        onSelect: () => downloadObjectDataPackage(templateId ?? ''),
      },
    ] as (MenuOption | false)[]
  ).filter((item): item is MenuOption => Boolean(item))

  return (
    <Page>
      <Page.Content>
        <Panel useContent={false} p={16}>
          <Flex direction="column" gap={16}>
            <PageHeader
              title={currentTemplate?.name ?? ''}
              action={
                <BasicMenu
                  aria-label="Actions"
                  modal={false}
                  menuOptions={menuOptions}
                >
                  <Button
                    aria-label="Actions"
                    data-testid="actions-menu"
                    iconRight={<KeyboardArrowDown />}
                  >
                    Actions
                  </Button>
                </BasicMenu>
              }
              breadcrumbItems={[
                {
                  label: 'Templates',
                  to: '/templates',
                },
                {
                  label: currentTemplate?.name ?? '',
                },
              ]}
              isLoading={isLoading}
            />
            {currentTemplate?.golden && (
              <Note color="warning" icon>
                <Flex direction="column">
                  <div>Golden Template</div>
                  <div>
                    This template carries the &quot;Golden&quot; designation,
                    meaning it should not be edited or deleted. Please take
                    caution.
                  </div>
                </Flex>
              </Note>
            )}
            <Typography variant="header-m">Overview</Typography>
            <Flex justify="space-between">
              <DetailItem
                label="Description"
                value={currentTemplate?.description}
              />
              <DetailItem
                label="Creation Time"
                value={formatDateTime(currentTemplate?.createdAt)}
              />
              <DetailItem
                label="Type"
                value={<TemplateStatusTag template={currentTemplate} />}
              />
              <Flex />
            </Flex>
          </Flex>
        </Panel>
        <Panel useContent={false}>
          <Flex direction="column" gap={16}>
            <Typography variant="header-m">Template Services</Typography>
            <Table<TemplateFragment>
              borderless
              data={fragments}
              columns={fragmentColumns}
              isLoading={isLoading}
              empty={{
                title: 'There are no templates fragments.',
                Icon: Article,
              }}
            />
          </Flex>
        </Panel>
      </Page.Content>
      <UpdateTemplateModal
        modal={updateTemplateModal}
        template={currentTemplate}
      />
      <ViewTemplateModal modal={viewTemplateModal} template={currentTemplate} />
      <DeleteTemplateModal
        modal={deleteTemplateModal}
        template={currentTemplate}
      />
      <UpdateGoldenStatusModal
        modal={markGoldenModal}
        markAsGolden
        template={currentTemplate}
      />
      <UpdateGoldenStatusModal
        modal={unmarkGoldenModal}
        markAsGolden={false}
        template={currentTemplate}
      />
      <PublishTemplateModal
        modal={publishTemplateModal}
        template={currentTemplate}
      />
      <UploadObjectDataModal
        modal={uploadObjectDataModal}
        template={currentTemplate}
      />
    </Page>
  )
}
